import styled, { css, keyframes } from 'styled-components';

import { DURATION } from '@polyai/common/utils/animationDuration';
import Clickable from '@polyai/ui/components/atoms/Clickable';
import {
  Body,
  BodyStrong,
  Subtitle,
} from '@polyai/ui/components/atoms/LegacyText';
import { ConfirmationModal } from '@polyai/ui/components/molecules/ConfirmationModal';
import { PersistentScrollbarStyles } from '@polyai/ui/styles/components/scrollbar';
import themeColors from '@polyai/ui/styles/config/colors';
import sizes from '@polyai/ui/styles/config/sizes';
import themeSpacing, { spacing } from '@polyai/ui/styles/config/spacing';
import text from '@polyai/ui/styles/config/text';

const MODAL_WIDTH = 400;
const ACTION_POP_UP_WIDTH = 320;

export const Wrapper = styled.div`
  position: sticky;
  z-index: 20;
  top: 0;
  background-color: ${themeColors.background};
`;

export const Header = styled.header<{ $useDarkTheme?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;

  height: ${sizes.headerHeight}px;

  border-bottom: 1px solid ${themeColors.borderSubtle};

  ${({ $useDarkTheme = false }) =>
    $useDarkTheme &&
    css`
      background: ${themeColors.backgroundInverse};
      border-bottom: 1px solid ${themeColors.borderSubtleInverse};
    `}
`;

export const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
`;

export const ProjectSettings = styled.div<{ $isMainNavOpen: boolean }>`
  transform: ${({ $isMainNavOpen }) =>
    $isMainNavOpen
      ? `translateX(calc(${sizes.mainNavWidth}px + 24px))`
      : `translateX(${sizes.headerHeight}px)`};

  display: flex;
  gap: ${themeSpacing.xs2};
  align-items: center;

  padding: 0 ${themeSpacing.sm3} 0 0;

  transition: transform 150ms linear;
`;

export const MenuButtonContainer = styled.div<{ $useDarkTheme?: boolean }>`
  position: absolute;
  z-index: 20;
  left: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  width: ${sizes.headerHeight}px;
  height: ${sizes.headerHeight}px;

  color: ${({ $useDarkTheme }) =>
    $useDarkTheme ? themeColors.iconPrimaryInverse : themeColors.iconPrimary};
`;

export const UnsavedChangesModal = styled(ConfirmationModal)`
  &-content {
    width: ${MODAL_WIDTH}px;
  }
`;

export const NumberClickable = styled(Clickable)<{
  $disabled?: boolean;
  $useDarkTheme?: boolean;
}>`
  display: flex;
  gap: ${themeSpacing.xs1};
  align-items: center;

  font-size: ${text.size.body.regular};
  font-weight: ${text.weight.semibold};

  &:not(:last-child) {
    margin-right: ${themeSpacing.xs2};
  }

  ${({ $disabled }) =>
    $disabled &&
    css`
      cursor: default;
      color: ${themeColors.textDisabled} !important;
    `}

  ${({ $useDarkTheme = false }) =>
    $useDarkTheme &&
    css`
      color: ${themeColors.textPrimaryInverse};
    `}
`;

export const HeaderRight = styled.div`
  display: flex;
  gap: ${themeSpacing.xs2};
  align-items: center;
  margin-right: ${themeSpacing.sm3};
`;

export const PhoneNumLoadingWrapper = styled.div`
  display: flex;
  align-items: center;
`;

export const ActionHeader = styled.header<{
  $open?: boolean;
}>`
  overflow: hidden;
  height: ${sizes.actionHeaderHeight}px;
  max-height: 0;
  transition: all ${DURATION.FAST}s ease-in-out;

  ${({ $open }) =>
    $open &&
    css`
      max-height: ${sizes.actionHeaderHeight}px;
      border-bottom: 1px solid ${themeColors.borderSubtle};
    `}
`;

export const ActionHeaderContents = styled.div<{ $mainNavOpen: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${themeSpacing.xs2} ${themeSpacing.sm3};

  ${({ $mainNavOpen }) =>
    $mainNavOpen &&
    css`
      padding-left: calc(${sizes.mainNavWidth}px + ${themeSpacing.sm3});
    `};
`;

export const ActionHeaderButtons = styled.div`
  display: flex;
  gap: ${themeSpacing.xs1};
  align-items: center;
`;

export const AgentName = styled(BodyStrong)<{ $useDarkTheme?: boolean }>`
  color: ${({ $useDarkTheme }) =>
    $useDarkTheme ? themeColors.textPrimaryInverse : themeColors.textPrimary};
`;

export const LanguageName = styled(Body)<{ $useDarkTheme?: boolean }>`
  align-items: center;
  color: ${themeColors.textHelper};
`;

export const SavingContainer = styled(Body)<{ $saved: boolean }>`
  display: flex;
  gap: ${themeSpacing.xs1};
  align-items: center;

  ${({ $saved }) =>
    $saved &&
    css`
      color: ${themeColors.textSuccess};
    `}
`;

export const ActionPopUp = styled.div<{ $isPhoneNumbersPopUp?: boolean }>`
  position: absolute;
  z-index: 1;
  top: ${themeSpacing.lg2};
  right: 4.5rem;

  display: flex;
  flex-direction: column;
  gap: ${themeSpacing.sm1};
  justify-content: space-between;

  width: ${ACTION_POP_UP_WIDTH}px;
  max-height: calc(100vh - ${sizes.headerHeight}px - ${spacing.sm2});

  background: ${themeColors.backgroundInverse};
  border: 1px solid;
  border-radius: ${themeSpacing.radiusMedium};
  ${({ $isPhoneNumbersPopUp }) =>
    $isPhoneNumbersPopUp &&
    css`
      top: ${themeSpacing.md3};
    `}
`;

export const ActionPopUpContent = styled.div`
  overflow-y: auto;
  overscroll-behavior: contain;
  flex: 1;
  padding: ${themeSpacing.sm1};
  ${PersistentScrollbarStyles};
`;

export const ActionPopUpHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  padding: ${themeSpacing.sm1};
  padding-bottom: 0;
`;

export const ActionPopUpTitle = styled(Subtitle)`
  font-size: ${text.size.body.regular};
  font-weight: ${text.weight.semibold};
  color: ${themeColors.textSecondaryInverse};
`;

export const PreviewHeader = styled.header<{
  $open?: boolean;
}>`
  z-index: 30;

  overflow: hidden;

  max-height: 0;

  background-color: ${themeColors.iconSecondary};

  transition: all ${DURATION.FAST}s ease-in-out;

  ${({ $open }) =>
    $open &&
    css`
      max-height: ${sizes.actionHeaderHeight}px;
    `}
`;

export const PreviewHeaderContents = styled.div<{ $mainNavOpen: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${themeSpacing.xs2} ${themeSpacing.sm3};

  ${({ $mainNavOpen }) =>
    $mainNavOpen &&
    css`
      padding-left: calc(${sizes.mainNavWidth}px + ${themeSpacing.sm3});
    `};
`;

export const SampleProjectTags = styled.div`
  display: flex;
  gap: ${themeSpacing.xs2};
  justify-content: center;
`;

// Skeleton-related styles
const shimmer = keyframes`
  0%, 100% { opacity: 0.7; }
  50% { opacity: 1; }
`;

export const SkeletonWrapper = styled.div`
  position: sticky;
  z-index: 900;
  top: 0;

  flex: 1;

  background-color: ${themeColors.background};
`;

export const ShimmerContainer = styled.div`
  display: contents;
  animation: ${shimmer} 2s ease-in-out infinite;
`;

export const ActionHeaderSkeleton = styled.div`
  display: flex;
  align-items: center;

  width: 100%;
  height: ${sizes.actionHeaderHeight}px;
  padding: ${themeSpacing.xs2} ${themeSpacing.sm3};

  border-bottom: 1px solid ${themeColors.borderSubtle};
`;
